/**
 * Trovva — Login Page
 *
 * Clean, modern login with OAuth providers (Google, Apple, Microsoft)
 * plus email/password fallback. Forgot password flow included.
 */
import { useState } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { theme } from '../lib/theme';
import { supabase } from '../lib/supabase';

// ── OAuth SVG icons (inline to avoid external deps) ──────────
const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const AppleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);

const MicrosoftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
    <rect x="13" y="1" width="10" height="10" fill="#7FBA00"/>
    <rect x="1" y="13" width="10" height="10" fill="#00A4EF"/>
    <rect x="13" y="13" width="10" height="10" fill="#FFB900"/>
  </svg>
);

export default function LoginPage({ onLogin, onLegal }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(null); // 'google' | 'apple' | 'microsoft'
  const [showEmailForm, setShowEmailForm] = useState(false);

  // Forgot password
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');

  const handleOAuth = async (provider) => {
    setOauthLoading(provider);
    setError('');
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: window.location.origin,
          queryParams: provider === 'google' ? { access_type: 'offline', prompt: 'consent' } : undefined,
        },
      });
      if (error) throw error;
    } catch (err) {
      setError(err.message || `Failed to sign in with ${provider}`);
      setOauthLoading(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await onLogin(email, password, isSignUp ? { full_name: fullName } : null);
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally { setLoading(false); }
  };

  const handleDemo = () => onLogin(null, null, null, true);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    if (!resetEmail.trim()) { setResetError('Please enter your email address'); return; }
    setResetLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) setResetError(error.message);
      else setResetSent(true);
    } catch (err) { setResetError('Failed to send reset email. Please try again.'); }
    setResetLoading(false);
  };

  // ── Styles ──
  const inputStyle = {
    width: '100%', padding: '11px 14px', borderRadius: 10,
    border: `1px solid ${theme.colors.border}`, fontSize: 14,
    outline: 'none', boxSizing: 'border-box', background: '#fff',
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: theme.colors.textLight, display: 'block', marginBottom: 4 };
  const oauthBtn = (isLoading) => ({
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
    padding: '12px 16px', borderRadius: 10, border: `1px solid ${theme.colors.border}`,
    background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: theme.colors.text,
    opacity: isLoading ? 0.6 : 1, transition: 'all 0.15s',
  });
  const primaryBtn = (disabled) => ({
    width: '100%', padding: '12px', borderRadius: 10, border: 'none',
    background: theme.colors.primary, color: '#fff',
    fontSize: 15, fontWeight: 600, cursor: 'pointer',
    opacity: disabled ? 0.6 : 1,
  });

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: `linear-gradient(135deg, ${theme.colors.primaryDark} 0%, ${theme.colors.primary} 50%, ${theme.colors.primaryLight} 100%)`,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    }}>
      <div style={{
        background: '#fff', borderRadius: 20, padding: '40px 36px',
        width: 420, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14,
            background: `linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.accentLight})`,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, color: '#fff', fontSize: 28, marginBottom: 10,
          }}>T</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: theme.colors.text }}>Trovva</div>
          <div style={{ fontSize: 13, color: theme.colors.textLight, marginTop: 2 }}>
            {isSignUp ? 'Create your account' : 'Welcome back'}
          </div>
        </div>

        {/* ═══ FORGOT PASSWORD ═══ */}
        {showForgotPassword ? (
          <div>
            {resetSent ? (
              <div>
                <div style={{
                  padding: 16, borderRadius: 12, marginBottom: 16, textAlign: 'center',
                  background: `${theme.colors.success}10`, border: `1px solid ${theme.colors.success}30`,
                }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>&#9993;</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: theme.colors.success, marginBottom: 6 }}>Reset Link Sent!</div>
                  <div style={{ fontSize: 13, color: theme.colors.text, lineHeight: 1.5 }}>
                    Check your inbox for <strong>{resetEmail}</strong>.
                  </div>
                </div>
                <button onClick={() => { setShowForgotPassword(false); setResetSent(false); }} style={primaryBtn(false)}>
                  Back to Sign In
                </button>
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text, marginBottom: 4 }}>Reset Your Password</div>
                  <div style={{ fontSize: 13, color: theme.colors.textLight, lineHeight: 1.5 }}>
                    Enter your email and we'll send you a reset link.
                  </div>
                </div>
                <form onSubmit={handleForgotPassword}>
                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Email Address</label>
                    <input type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)}
                      placeholder="you@brokerage.com" autoFocus style={inputStyle} />
                  </div>
                  {resetError && <div style={{ padding: '10px 14px', borderRadius: 8, background: `${theme.colors.danger}10`, color: theme.colors.danger, fontSize: 13, marginBottom: 14 }}>{resetError}</div>}
                  <button type="submit" disabled={resetLoading} style={{ ...primaryBtn(resetLoading), marginBottom: 12 }}>
                    {resetLoading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </form>
                <div style={{ textAlign: 'center' }}>
                  <button onClick={() => { setShowForgotPassword(false); setResetError(''); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.primary, fontSize: 13, fontWeight: 500 }}>
                    Back to Sign In
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* ═══ OAUTH BUTTONS ═══ */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              <button onClick={() => handleOAuth('google')} disabled={!!oauthLoading} style={oauthBtn(oauthLoading === 'google')}
                onMouseEnter={e => { if (!oauthLoading) e.currentTarget.style.background = '#F8FAFC'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}>
                {oauthLoading === 'google' ? <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} /> : <GoogleIcon />}
                Continue with Google
              </button>

              <button onClick={() => handleOAuth('apple')} disabled={!!oauthLoading} style={{ ...oauthBtn(oauthLoading === 'apple'), background: '#000', color: '#fff', border: '1px solid #000' }}
                onMouseEnter={e => { if (!oauthLoading) e.currentTarget.style.background = '#1a1a1a'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#000'; }}>
                {oauthLoading === 'apple' ? <Loader2 size={20} style={{ animation: 'spin 1s linear infinite', color: '#fff' }} /> : <AppleIcon />}
                Continue with Apple
              </button>

              <button onClick={() => handleOAuth('azure')} disabled={!!oauthLoading} style={oauthBtn(oauthLoading === 'azure')}
                onMouseEnter={e => { if (!oauthLoading) e.currentTarget.style.background = '#F8FAFC'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}>
                {oauthLoading === 'azure' ? <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} /> : <MicrosoftIcon />}
                Continue with Microsoft
              </button>
            </div>

            {/* ── Divider ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0' }}>
              <div style={{ flex: 1, height: 1, background: theme.colors.border }} />
              <span style={{ fontSize: 12, color: theme.colors.textLight, fontWeight: 500 }}>or</span>
              <div style={{ flex: 1, height: 1, background: theme.colors.border }} />
            </div>

            {/* ── Email/Password toggle ── */}
            {!showEmailForm ? (
              <button onClick={() => setShowEmailForm(true)} style={{
                width: '100%', padding: '11px', borderRadius: 10,
                border: `1px solid ${theme.colors.border}`, background: '#fff',
                cursor: 'pointer', fontSize: 14, fontWeight: 500, color: theme.colors.textLight,
                marginBottom: 12,
              }}>
                {isSignUp ? 'Sign up with email' : 'Sign in with email'}
              </button>
            ) : (
              <form onSubmit={handleSubmit}>
                {isSignUp && (
                  <div style={{ marginBottom: 12 }}>
                    <label style={labelStyle}>Full Name</label>
                    <input value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Jane Smith" style={inputStyle} />
                  </div>
                )}
                <div style={{ marginBottom: 12 }}>
                  <label style={labelStyle}>Email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@brokerage.com" style={inputStyle} />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={labelStyle}>Password</label>
                  <div style={{ position: 'relative' }}>
                    <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                      placeholder="••••••••" style={{ ...inputStyle, paddingRight: 42 }} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} style={{
                      position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: theme.colors.textLight, display: 'flex',
                    }}>
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                {!isSignUp && (
                  <div style={{ textAlign: 'right', marginBottom: 14 }}>
                    <button type="button" onClick={() => { setShowForgotPassword(true); setResetEmail(email); setError(''); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.primary, fontSize: 12, fontWeight: 500, padding: 0 }}>
                      Forgot password?
                    </button>
                  </div>
                )}
                {error && <div style={{ padding: '10px 14px', borderRadius: 8, background: `${theme.colors.danger}10`, color: theme.colors.danger, fontSize: 13, marginBottom: 14 }}>{error}</div>}
                <button type="submit" disabled={loading} style={{ ...primaryBtn(loading), marginBottom: 10 }}>
                  {loading ? 'Please wait...' : isSignUp ? 'Create Account' : 'Sign In'}
                </button>
              </form>
            )}

            {/* Demo + toggle */}
            <button onClick={handleDemo} style={{
              width: '100%', padding: '10px', borderRadius: 10,
              border: `2px solid ${theme.colors.accent}`, background: '#fff',
              color: theme.colors.accent, fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 14,
            }}>
              Try Demo Mode
            </button>

            <div style={{ textAlign: 'center' }}>
              <button onClick={() => { setIsSignUp(!isSignUp); setError(''); setShowEmailForm(false); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.primary, fontSize: 13, fontWeight: 500 }}>
                {isSignUp ? 'Already have an account? Sign in' : 'Need an account? Sign up'}
              </button>
            </div>

            {/* Legal */}
            <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${theme.colors.border}`, textAlign: 'center' }}>
              <p style={{ fontSize: 11, color: theme.colors.textLight, lineHeight: 1.6, margin: 0 }}>
                By continuing, you agree to our{' '}
                <button type="button" onClick={() => onLegal?.('terms')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: theme.colors.primary, fontSize: 11, fontWeight: 500, textDecoration: 'underline' }}>
                  Terms of Service
                </button>{' '}and{' '}
                <button type="button" onClick={() => onLegal?.('privacy')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: theme.colors.primary, fontSize: 11, fontWeight: 500, textDecoration: 'underline' }}>
                  Privacy Policy
                </button>.
              </p>
            </div>
          </>
        )}
        <style>{`@keyframes spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }`}</style>
      </div>
    </div>
  );
}
